/**
 * Admin Mode Context - 管理模式状态管理
 * 提供管理模式的进入、退出、状态查询、心跳保活等功能
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from 'react';
import { Modal, Button, Space } from 'antd';
import { message } from '@/utils/antdStatic';
import { ExclamationCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import adminModeApi, { AdminModeStatus, AdminModeSettings, TenantSettings, HeartbeatResponse, EnterAdminModeResponse, ExitAdminModeResponse } from '../api/adminMode';
import { getStorageItem, setStorageItem, getToken, getUserInfo, STORAGE_KEYS } from '@/utils/storage';

/** Helper type for handleApiResponse results */
type ApiResult<T> = { data: T; total: number; success: boolean; message?: string };

/** 响应拦截器会把后端 snake_case 字段转成 camelCase，两种写法都可能出现 */
type CamelFields = {
  isInAdminMode?: boolean;
  hasPermission?: boolean;
  sessionId?: number;
  enteredAt?: string;
  timeoutMinutes?: number;
  remainingSeconds?: number;
};
type LooseStatus = Partial<AdminModeStatus> & CamelFields;

// 本地存储键
const ADMIN_MODE_KEY = 'admin_mode_status';
const BUSINESS_LOCATION_KEY = 'business_mode_location';  // 业务模式最后位置
const ADMIN_LOCATION_KEY = 'admin_mode_location';        // 管理模式最后位置
// 管理模式会话在服务端按账号共享，界面只在自己进入过的窗口里显示管理模式，
// 避免一个窗口进管理模式后把其他窗口的业务页面也带进去
const WINDOW_ADMIN_MODE_KEY = 'admin_mode_window';
const isWindowInAdminMode = () => sessionStorage.getItem(WINDOW_ADMIN_MODE_KEY) === '1';

/**
 * 检查用户是否完全认证（有token且有tenant_id）
 * 用于确保API请求能带上必要的headers
 */
const isFullyAuthenticated = (): boolean => {
  const token = getToken();
  if (!token) return false;

  const userInfo = getUserInfo();
  if (userInfo?.tenant_id) return true;

  // 如果userInfo没有tenant_id，尝试从JWT解析
  try {
    const parts = token.split('.');
    if (parts.length === 3) {
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      if (payload?.tenant_id) return true;
    }
  } catch {
    // 解析失败
  }

  return false;
};

interface AdminModeContextProps {
  // 状态
  isInAdminMode: boolean;
  isLoading: boolean;
  hasPermission: boolean;
  remainingSeconds: number;
  timeoutMinutes: number;
  sessionId?: number;
  enteredAt?: string;
  tenantSettings?: TenantSettings;
  showTimeoutWarning: boolean;
  isExtending: boolean;
  /** 状态是否已从服务端加载完成（用于路由守卫判断） */
  isStatusLoaded: boolean;

  // 方法
  enterAdminMode: () => Promise<boolean>;
  exitAdminMode: (reason?: string) => Promise<boolean>;
  extendSession: () => Promise<boolean>;
  checkStatus: () => Promise<void>;
  refreshSettings: () => Promise<void>;
  setOnTimeoutCallback: (callback: () => void) => void;
  dismissTimeoutWarning: () => void;

  // 页面位置记忆 - 业务模式
  saveBusinessLocation: (location: string) => void;
  getBusinessLocation: () => string | null;
  clearBusinessLocation: () => void;

  // 页面位置记忆 - 管理模式
  saveAdminLocation: (location: string) => void;
  getAdminLocation: () => string | null;
  clearAdminLocation: () => void;

  // 自动保存当前位置（根据模式自动选择）
  saveCurrentLocation: (location: string) => void;
}

const AdminModeContext = createContext<AdminModeContextProps | undefined>(undefined);

// 心跳间隔（毫秒）
const HEARTBEAT_INTERVAL = 60 * 1000; // 1分钟

// 超时警告阈值（秒）
const TIMEOUT_WARNING_THRESHOLD = 300; // 5分钟 - 普通警告
const TIMEOUT_CRITICAL_THRESHOLD = 60; // 1分钟 - 危险警告Modal

// 从 localStorage 同步初始化状态（避免首次渲染时路由守卫卡住）
const getInitialStateFromCache = () => {
  try {
    const cached = getStorageItem<LooseStatus>(ADMIN_MODE_KEY);
    if (cached) {
      return {
        isInAdminMode: Boolean(cached.is_in_admin_mode || cached.isInAdminMode) && isWindowInAdminMode(),
        hasPermission: cached.has_permission ?? cached.hasPermission ?? false,
        sessionId: cached.session_id || cached.sessionId,
        enteredAt: cached.entered_at || cached.enteredAt,
        timeoutMinutes: cached.timeout_minutes || cached.timeoutMinutes || 30,
        isStatusLoaded: (cached.has_permission !== undefined || cached.hasPermission !== undefined),
      };
    }
  } catch (e) {
    console.warn('Failed to read admin mode cache:', e);
  }
  return {
    isInAdminMode: false,
    hasPermission: false,
    sessionId: undefined,
    enteredAt: undefined,
    timeoutMinutes: 30,
    isStatusLoaded: false,
  };
};

export const AdminModeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // 同步从 localStorage 初始化状态
  const initialState = getInitialStateFromCache();

  const [isInAdminMode, setIsInAdminMode] = useState(initialState.isInAdminMode);
  const [isLoading, setIsLoading] = useState(false);
  const [hasPermission, setHasPermission] = useState(initialState.hasPermission);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [timeoutMinutes, setTimeoutMinutes] = useState(initialState.timeoutMinutes);
  const [sessionId, setSessionId] = useState<number | undefined>(initialState.sessionId);
  const [enteredAt, setEnteredAt] = useState<string | undefined>(initialState.enteredAt);
  const [tenantSettings, setTenantSettings] = useState<TenantSettings | undefined>();
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [isExtending, setIsExtending] = useState(false);
  const [isStatusLoaded, setIsStatusLoaded] = useState(initialState.isStatusLoaded); // 从缓存同步初始化

  const heartbeatTimer = useRef<NodeJS.Timeout | null>(null);
  const countdownTimer = useRef<NodeJS.Timeout | null>(null);
  const warningShown = useRef(false);
  const criticalWarningShown = useRef(false);
  const onTimeoutCallbackRef = useRef<(() => void) | null>(null);
  const isExitingRef = useRef(false); // 防止退出过程中 checkStatus 触发重入

  // 清理定时器
  const clearTimers = useCallback(() => {
    if (heartbeatTimer.current) {
      clearInterval(heartbeatTimer.current);
      heartbeatTimer.current = null;
    }
    if (countdownTimer.current) {
      clearInterval(countdownTimer.current);
      countdownTimer.current = null;
    }
  }, []);

  // 更新状态到本地存储
  const updateLocalStorage = useCallback((status: Partial<AdminModeStatus>) => {
    const current = getStorageItem(ADMIN_MODE_KEY) || {};
    setStorageItem(ADMIN_MODE_KEY, { ...current, ...status });
  }, []);

  // 清除本地存储
  const clearLocalStorage = useCallback(() => {
    localStorage.removeItem(ADMIN_MODE_KEY);
  }, []);

  // 内部退出管理模式（不发送API请求）
  const exitAdminModeInternal = useCallback((reason: string) => {
    isExitingRef.current = true;
    setIsInAdminMode(false);
    setSessionId(undefined);
    setEnteredAt(undefined);
    setRemainingSeconds(0);
    setShowTimeoutWarning(false);
    clearTimers();
    clearLocalStorage();
    sessionStorage.removeItem(WINDOW_ADMIN_MODE_KEY);

    if (reason === 'timeout') {
      message.info('管理模式已超时自动退出');
      // 执行超时回调（跳转到业务首页）
      if (onTimeoutCallbackRef.current) {
        onTimeoutCallbackRef.current();
      }
    }
  }, [clearTimers, clearLocalStorage]);

  // 启动倒计时
  const startCountdown = useCallback((seconds: number) => {
    setRemainingSeconds(seconds);
    warningShown.current = false;
    criticalWarningShown.current = false;
    setShowTimeoutWarning(false);

    if (countdownTimer.current) {
      clearInterval(countdownTimer.current);
    }

    countdownTimer.current = setInterval(() => {
      setRemainingSeconds(prev => {
        const newValue = prev - 1;

        // 5分钟警告 - 消息提示
        if (newValue === TIMEOUT_WARNING_THRESHOLD && !warningShown.current) {
          warningShown.current = true;
          message.warning(`管理模式将在 ${Math.floor(TIMEOUT_WARNING_THRESHOLD / 60)} 分钟后自动退出`);
        }

        // 1分钟危险警告 - Modal弹窗
        if (newValue === TIMEOUT_CRITICAL_THRESHOLD && !criticalWarningShown.current) {
          criticalWarningShown.current = true;
          setShowTimeoutWarning(true);
        }

        // 超时退出
        if (newValue <= 0) {
          setShowTimeoutWarning(false);
          exitAdminModeInternal('timeout');
          return 0;
        }

        return newValue;
      });
    }, 1000);
  }, [exitAdminModeInternal]);

  // 延长会话时间（通过心跳重置）
  const extendSession = useCallback(async (): Promise<boolean> => {
    setIsExtending(true);
    try {
      const resp = await adminModeApi.heartbeat() as unknown as ApiResult<HeartbeatResponse>;
      if (resp?.success && resp.data?.success) {
        // 兼容camelCase和snake_case
        const data = resp.data as HeartbeatResponse & CamelFields;
        const newRemaining = data.remainingSeconds ?? data.remaining_seconds;
        setRemainingSeconds(newRemaining);
        setShowTimeoutWarning(false);
        warningShown.current = false;
        criticalWarningShown.current = false;
        message.success('会话已延长');
        return true;
      } else {
        message.error('延长会话失败');
        return false;
      }
    } catch (error) {
      console.error('延长会话失败:', error);
      message.error('延长会话失败');
      return false;
    } finally {
      setIsExtending(false);
    }
  }, []);

  // 设置超时回调
  const setOnTimeoutCallback = useCallback((callback: () => void) => {
    onTimeoutCallbackRef.current = callback;
  }, []);

  // 启动心跳
  const startHeartbeat = useCallback(() => {
    if (heartbeatTimer.current) {
      clearInterval(heartbeatTimer.current);
    }

    heartbeatTimer.current = setInterval(async () => {
      try {
        const resp = await adminModeApi.heartbeat() as unknown as ApiResult<HeartbeatResponse>;
        if (resp?.success && resp.data?.success) {
          // 兼容camelCase和snake_case
          const data = resp.data as HeartbeatResponse & CamelFields;
          const newRemaining = data.remainingSeconds ?? data.remaining_seconds;
          setRemainingSeconds(newRemaining);
          warningShown.current = false; // 重置警告标志
        } else {
          // 心跳失败，退出管理模式
          exitAdminModeInternal('heartbeat_failed');
        }
      } catch {
        console.error('心跳失败');
        // 不立即退出，等待下次心跳
      }
    }, HEARTBEAT_INTERVAL);
  }, [exitAdminModeInternal]);

  // 检查管理模式状态
  const checkStatus = useCallback(async () => {
    // 退出过程中跳过状态检查，防止竞态导致重入
    if (isExitingRef.current) {
      return;
    }
    try {
      const resp = await adminModeApi.getStatus() as unknown as ApiResult<AdminModeStatus>;
      if (resp?.success && resp.data) {
        const status = resp.data as AdminModeStatus & CamelFields;
        // 注意：响应数据经过request.ts的camelCase转换，所以使用camelCase属性名
        // 同时兼容snake_case（防止某些情况下未转换）
        const hasPermissionValue = status.hasPermission ?? status.has_permission;
        const timeoutMinutesValue = status.timeoutMinutes ?? status.timeout_minutes;
        const isInAdminModeValue = status.isInAdminMode ?? status.is_in_admin_mode;
        const sessionIdValue = status.sessionId ?? status.session_id;
        const enteredAtValue = status.enteredAt ?? status.entered_at;
        const remainingSecondsValue = status.remainingSeconds ?? status.remaining_seconds;

        setHasPermission(hasPermissionValue);
        setTimeoutMinutes(timeoutMinutesValue);
        setIsStatusLoaded(true); // 标记已成功加载

        if (isInAdminModeValue && isWindowInAdminMode()) {
          setIsInAdminMode(true);
          setSessionId(sessionIdValue);
          setEnteredAt(enteredAtValue);
          setRemainingSeconds(remainingSecondsValue || 0);
          updateLocalStorage(status);

          // 启动心跳和倒计时
          startHeartbeat();
          startCountdown(remainingSecondsValue || timeoutMinutesValue * 60);
        } else {
          setIsInAdminMode(false);
          setSessionId(undefined);
          setEnteredAt(undefined);
          setRemainingSeconds(0);
          clearTimers();
          // 其他窗口还在管理模式时，共享的本地缓存留给它们
          if (!isInAdminModeValue) {
            clearLocalStorage();
            sessionStorage.removeItem(WINDOW_ADMIN_MODE_KEY);
          }
        }
      }
    } catch (error) {
      console.warn('检查管理模式状态失败（foundation-service可能未启动），使用默认状态:', error);
      // 使用默认状态，不阻塞登录流程
      setHasPermission(false);
      setTimeoutMinutes(30);
      setIsStatusLoaded(true);
      setIsInAdminMode(false);
    }
  }, [updateLocalStorage, startHeartbeat, startCountdown, clearTimers, clearLocalStorage]);

  // 获取租户配置
  const refreshSettings = useCallback(async () => {
    try {
      const resp = await adminModeApi.getTenantSettings() as unknown as ApiResult<TenantSettings>;
      if (resp?.success && resp.data) {
        setTenantSettings(resp.data);
        const adminModeSettings: (AdminModeSettings & CamelFields) | undefined =
          (resp.data as TenantSettings & { adminMode?: AdminModeSettings }).adminMode ?? resp.data.admin_mode;
        if (adminModeSettings) {
          setTimeoutMinutes(adminModeSettings.timeoutMinutes ?? adminModeSettings.timeout_minutes ?? 30);
        }
      }
    } catch (error) {
      console.warn('获取租户配置失败（foundation-service可能未启动），使用默认配置:', error);
      // 使用默认配置，不阻塞登录流程
      setTenantSettings({
        admin_mode: {
          enabled: false,
          timeout_minutes: 30,
        },
        security: {
          password_min_length: 6,
          password_require_special: false,
          login_fail_lock_count: 5,
          login_fail_lock_minutes: 30,
        },
        theme: {
          primary_color: '#1890ff',
          admin_primary_color: '#ff4d4f',
        },
        branding: {
          system_name: 'Coolbuy 3.0',
          logo_url: '',
          favicon_url: '',
          copyright: '© 2024 Coolbuy',
        },
      });
    }
  }, []);

  // 进入管理模式
  const enterAdminMode = useCallback(async (): Promise<boolean> => {
    // 防止退出后立即重入（退出流程 2 秒内禁止进入）
    if (isExitingRef.current) {
      console.warn('AdminMode: 退出流程中，忽略进入请求');
      return false;
    }

    if (!hasPermission) {
      message.error('您没有进入管理模式的权限');
      return false;
    }

    setIsLoading(true);
    try {
      const resp = await adminModeApi.enter() as unknown as ApiResult<EnterAdminModeResponse>;
      if (resp?.success && resp.data?.success) {
        const data = resp.data as EnterAdminModeResponse & CamelFields;
        // 注意：响应数据经过request.ts的camelCase转换，兼容两种格式
        const sessionIdValue = data.sessionId ?? data.session_id;
        const enteredAtValue = data.enteredAt ?? data.entered_at;
        const timeoutMinutesValue = data.timeoutMinutes ?? data.timeout_minutes;

        sessionStorage.setItem(WINDOW_ADMIN_MODE_KEY, '1');
        setIsInAdminMode(true);
        setSessionId(sessionIdValue);
        setEnteredAt(enteredAtValue);
        setTimeoutMinutes(timeoutMinutesValue);

        const initialSeconds = timeoutMinutesValue * 60;
        setRemainingSeconds(initialSeconds);

        updateLocalStorage({
          is_in_admin_mode: true,
          session_id: sessionIdValue,
          entered_at: enteredAtValue,
          timeout_minutes: timeoutMinutesValue,
          remaining_seconds: initialSeconds,
        });

        // 启动心跳和倒计时
        startHeartbeat();
        startCountdown(initialSeconds);

        message.success(data.message || '已进入管理模式');
        return true;
      } else {
        message.error(resp?.data?.message || '进入管理模式失败');
        return false;
      }
    } catch (error) {
      console.error('进入管理模式失败:', error);
      message.error('进入管理模式失败');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [hasPermission, updateLocalStorage, startHeartbeat, startCountdown]);

  // 退出管理模式
  const exitAdminMode = useCallback(async (reason: string = 'manual'): Promise<boolean> => {
    isExitingRef.current = true; // 阻止 checkStatus 竞态重入
    setIsLoading(true);
    try {
      const resp = await adminModeApi.exit(reason) as unknown as ApiResult<ExitAdminModeResponse>;
      if (resp?.success && resp.data?.success) {
        exitAdminModeInternal(reason);
        message.success(resp.data.message || '已退出管理模式');
        return true;
      } else {
        isExitingRef.current = false;
        message.error('退出管理模式失败');
        return false;
      }
    } catch (error) {
      isExitingRef.current = false;
      console.error('退出管理模式失败:', error);
      message.error('退出管理模式失败');
      return false;
    } finally {
      setIsLoading(false);
      // 延迟清除标志，确保退出后不会被 checkStatus 或 enterAdminMode 竞态重入
      setTimeout(() => { isExitingRef.current = false; }, 3000);
    }
  }, [exitAdminModeInternal]);

  // 页面位置记忆 - 业务模式
  const saveBusinessLocation = useCallback((location: string) => {
    // 只保存业务模式路径（非 /tenant/admin/* 路径）
    if (!location.startsWith('/tenant/admin')) {
      localStorage.setItem(BUSINESS_LOCATION_KEY, location);
    }
  }, []);

  const getBusinessLocation = useCallback((): string | null => {
    return localStorage.getItem(BUSINESS_LOCATION_KEY);
  }, []);

  const clearBusinessLocation = useCallback(() => {
    localStorage.removeItem(BUSINESS_LOCATION_KEY);
  }, []);

  // 页面位置记忆 - 管理模式
  const saveAdminLocation = useCallback((location: string) => {
    // 只保存管理模式路径（/tenant/admin/* 路径）
    if (location.startsWith('/tenant/admin')) {
      localStorage.setItem(ADMIN_LOCATION_KEY, location);
    }
  }, []);

  const getAdminLocation = useCallback((): string | null => {
    return localStorage.getItem(ADMIN_LOCATION_KEY);
  }, []);

  const clearAdminLocation = useCallback(() => {
    localStorage.removeItem(ADMIN_LOCATION_KEY);
  }, []);

  // 自动保存当前位置（根据路径自动判断模式）
  const saveCurrentLocation = useCallback((location: string) => {
    if (location.startsWith('/tenant/admin')) {
      saveAdminLocation(location);
    } else if (location.startsWith('/tenant')) {
      saveBusinessLocation(location);
    }
  }, [saveAdminLocation, saveBusinessLocation]);

  // 关闭超时警告弹窗
  const dismissTimeoutWarning = useCallback(() => {
    setShowTimeoutWarning(false);
  }, []);

  // 处理手动退出（从警告弹窗）
  const handleManualExit = useCallback(async () => {
    setShowTimeoutWarning(false);
    await exitAdminMode('manual');
  }, [exitAdminMode]);

  // 格式化剩余时间显示
  const formatRemainingTime = (seconds: number): string => {
    if (seconds <= 0) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // 初始化 - 从本地存储恢复状态并检查服务端状态
  useEffect(() => {
    const initialize = async () => {
      // 检查是否完全认证（有token和tenant_id）
      if (!isFullyAuthenticated()) {
        console.log('AdminModeContext: 用户未完全认证，跳过状态检查');
        // 未认证时也标记状态已加载，避免路由守卫卡住
        setIsStatusLoaded(true);
        return;
      }

      // 尝试从本地存储恢复
      const cached = getStorageItem<LooseStatus>(ADMIN_MODE_KEY);
      if ((cached?.is_in_admin_mode || cached?.isInAdminMode) && isWindowInAdminMode()) {
        setIsInAdminMode(true);
        setSessionId(cached.session_id || cached.sessionId);
        setEnteredAt(cached.entered_at || cached.enteredAt);
        setTimeoutMinutes(cached.timeout_minutes || cached.timeoutMinutes || 30);
        // 如果有本地缓存且包含权限信息，先标记状态已加载
        if (cached.has_permission !== undefined || cached.hasPermission !== undefined) {
          setHasPermission(cached.has_permission ?? cached.hasPermission ?? false);
          setIsStatusLoaded(true);
        }
      }

      // 检查服务端状态（会更新 isStatusLoaded）
      await checkStatus();
      await refreshSettings();
    };

    initialize();

    // 清理
    return () => {
      clearTimers();
    };
  }, [checkStatus, refreshSettings, clearTimers]);

  // 监听storage变化，用户登录后重新检查状态（跨标签页）
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      // 当token或用户信息变化时（登录/登出），重新检查状态
      if (e.key === STORAGE_KEYS.TOKEN || e.key === 'access_token' || e.key === STORAGE_KEYS.USER_INFO) {
        if (isFullyAuthenticated()) {
          console.log('AdminModeContext: 检测到用户登录（跨标签页），检查管理模式状态');
          setIsStatusLoaded(false); // 重置状态以触发重新检查
          checkStatus();
          refreshSettings();
        } else {
          // 用户登出或信息不完整，重置状态
          setHasPermission(false);
          setIsInAdminMode(false);
          setIsStatusLoaded(false);
          clearTimers();
          clearLocalStorage();
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [checkStatus, refreshSettings, clearTimers, clearLocalStorage]);

  // 页面可见性变化时检查状态
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // 完全认证时检查状态（用户可能刚登录或页面切换回来）
        if (isFullyAuthenticated()) {
          checkStatus();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkStatus]);

  // 处理同一标签页登录后的场景
  // 当状态未加载且已完全认证时，尝试加载状态
  useEffect(() => {
    const checkAuthAndStatus = () => {
      // 如果已完全认证但还没有加载过状态，进行检查
      if (isFullyAuthenticated() && !isStatusLoaded) {
        console.log('AdminModeContext: 检测到已认证但未加载状态，开始检查');
        checkStatus();
        refreshSettings();
      }
    };

    // 延迟执行，确保用户信息已经保存
    const timer = setTimeout(checkAuthAndStatus, 200);
    return () => clearTimeout(timer);
  }, [isStatusLoaded, checkStatus, refreshSettings]);

  return (
    <AdminModeContext.Provider
      value={{
        isInAdminMode,
        isLoading,
        hasPermission,
        remainingSeconds,
        timeoutMinutes,
        sessionId,
        enteredAt,
        tenantSettings,
        showTimeoutWarning,
        isExtending,
        isStatusLoaded,
        enterAdminMode,
        exitAdminMode,
        extendSession,
        checkStatus,
        refreshSettings,
        setOnTimeoutCallback,
        dismissTimeoutWarning,
        saveBusinessLocation,
        getBusinessLocation,
        clearBusinessLocation,
        saveAdminLocation,
        getAdminLocation,
        clearAdminLocation,
        saveCurrentLocation,
      }}
    >
      {children}
      {/* 超时警告弹窗 */}
      <Modal
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: 20 }} />
            <span>管理模式即将超时</span>
          </Space>
        }
        open={showTimeoutWarning}
        closable={false}
        maskClosable={false}
        footer={null}
        centered
        width={400}
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <div style={{ marginBottom: 16 }}>
            <ClockCircleOutlined style={{ fontSize: 48, color: '#ff4d4f' }} />
          </div>
          <div style={{ fontSize: 24, fontWeight: 600, color: '#ff4d4f', marginBottom: 8 }}>
            {formatRemainingTime(remainingSeconds)}
          </div>
          <div style={{ color: '#666', marginBottom: 24 }}>
            管理模式将在上述时间后自动退出，请选择操作：
          </div>
          <Space size="middle">
            <Button
              type="primary"
              size="large"
              loading={isExtending}
              onClick={extendSession}
            >
              延长会话
            </Button>
            <Button
              size="large"
              danger
              onClick={handleManualExit}
            >
              立即退出
            </Button>
          </Space>
        </div>
      </Modal>
    </AdminModeContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAdminMode = () => {
  const ctx = useContext(AdminModeContext);
  if (!ctx) {
    throw new Error('useAdminMode must be used within AdminModeProvider');
  }
  return ctx;
};

export { AdminModeContext };
